import { I18n } from '@calendly/i18n'
import { createSelector } from 'reselect'

import { getUser } from 'common'
import { stripHtmlTags } from 'features/scoped_url_state'
import { TriggerKind } from 'shared/constants/send_from_calendly'
import { getContactDisplayName } from 'shared/utils/get_contact_display_name'

import { FlowKind } from './slice'

export const INVITEE_EXPERIENCE_SEND_FROM_CALENDLY_Q3 = 'invitee_experience_send_from_calendly_q3'

const baseSelector = ({ sendFromCalendly }) => sendFromCalendly

export const isOpenSelector = createSelector(baseSelector, ({ isOpen }) => isOpen)

export const backUpValuesSelector = createSelector(baseSelector, ({ backUpValues }) => backUpValues)
export const flowStepSelector = createSelector(baseSelector, ({ flowStep }) => flowStep)

export const withCloseConfirmationSelector = createSelector(baseSelector, ({ withCloseConfirmation }) => withCloseConfirmation)

export const eventTypeSelector = createSelector(baseSelector, ({ eventType }) => eventType)
export const emailLocaleSelector = createSelector(baseSelector, ({ emailLocale }) => emailLocale)

export const inviteeSelector = createSelector(baseSelector, ({ invitee }) => invitee)

export const sourceSelector = createSelector(baseSelector, ({ source }) => source)

export const trackingSourceSelector = createSelector(baseSelector, ({ trackingSource }) => trackingSource)

export const requestIdSelector = createSelector(baseSelector, ({ requestId }) => requestId)

export const contactSelector = createSelector(baseSelector, ({ contact }) => contact)

export const eventSelector = createSelector(baseSelector, ({ event }) => event)

export const flowKindSelector = createSelector(baseSelector, ({ flowKind }) => flowKind)

export const isInviteeOnlyRescheduleSelector = createSelector(baseSelector,
  ({ isInviteeOnlyReschedule }) => isInviteeOnlyReschedule)

export const recipientFieldsStatusSelector = createSelector(baseSelector, ({ recipientFieldsStatus }) => recipientFieldsStatus)

export const triggerKindSelector = createSelector(baseSelector, ({ triggerKind }) => triggerKind)

const singleUseLinkSelector = createSelector(baseSelector, ({ singleUseLink }) => singleUseLink)

const bookingUrlSelector = createSelector(
  eventTypeSelector,
  eventSelector,
  singleUseLinkSelector,
  flowKindSelector,
  triggerKindSelector,
  (eventType, event, singleUseLink, flowKind, triggerKind) => {
    const bookingUrl = singleUseLink?.bookingUrl || eventType?.booking_url || eventType?.bookingUrl

    switch (flowKind) {
      case FlowKind.reschedule:
      {
        const redeemQP = event.purchasedPackageUuid ? `&redeem=${event.purchasedPackageUuid}` : ''
        return `${bookingUrl}?share_attribution=send_from_calendly_reschedule${redeemQP}`
      }
      case FlowKind.contact:
        return `${bookingUrl}?share_attribution=send_from_calendly_contacts`
      case FlowKind.eventType:
        switch (triggerKind) {
          case TriggerKind.eventType:
            return `${bookingUrl}?share_attribution=send_from_calendly_et_card`
          default:
            return bookingUrl
        }
      case FlowKind.eventTypePreview:
        switch (triggerKind) {
          case TriggerKind.bookingPage:
            return `${bookingUrl}?share_attribution=send_from_calendly_et_preview`
          default:
            return bookingUrl
        }
      case FlowKind.schedulingLink:
        switch (triggerKind) {
          case TriggerKind.sul:
            return `${bookingUrl}?share_attribution=send_from_calendly_sul`
          default:
            return bookingUrl
        }
      default:
        return bookingUrl
    }
  }
)

export const initialFormValuesSelector = createSelector(
  baseSelector,
  bookingUrlSelector,
  (state) => getUser.select()(state).data,
  ({
    eventType,
    event,
    contact,
    invitee,
    isOpen,
    isToFieldLocked,
    flowKind,
    emailLocale,
    prefilledSubject,
    prefilledBody
  }, bookingUrl, currentUser) => {
    if (!isOpen) return null
    const contactOrInvitee = contact || invitee

    const to = contactOrInvitee ? [contactOrInvitee] : []
    const isReschedulingFlow = flowKind === FlowKind.reschedule
    const locale = emailLocale || I18n.locale

    // Sanitize contact name to prevent XSS when interpolated into email body (rendered as HTML in Quill)
    const contactDisplayName = getContactDisplayName(contactOrInvitee)
    const sanitizedContactName = contactDisplayName ? stripHtmlTags(contactDisplayName) : undefined

    let subject = eventType
      ? I18n.t('publisher.contacts.share.email_form.default_subject', { event_type_name: eventType.name, locale })
      : I18n.t('publisher.contacts.share.email_form.default_subject_no_et', { locale })

    if (isReschedulingFlow) {
      subject = I18n.t('publisher.rescheduling_flow.share_email_modal.subject', { locale })
    }

    let body = eventType
      ? I18n.t('publisher.send_from_calendly.email.body_with_contact', {
        contact_name: sanitizedContactName,
        url: bookingUrl,
        full_name: currentUser.name,
        locale
      })
      : I18n.t('publisher.contacts.share.email_form.default_body_no_et', {
        user_name: sanitizedContactName,
        locale
      })

    if (isReschedulingFlow) {
      body = I18n.t('publisher.rescheduling_flow.share_email_modal.default_body', {
        contact_name: sanitizedContactName,
        url: bookingUrl,
        full_name: currentUser.name,
        locale
      })
    }
    if (!contactOrInvitee) {
      body = I18n.t('publisher.send_from_calendly.email.body_without_contact', {
        url: bookingUrl,
        full_name: currentUser.name,
        locale
      })
    }

    const cc = isReschedulingFlow
      ? event
        .eventMemberships.filter(({ email }) => email !== currentUser.email)
        .slice(0, 10)
      : []

    const idFieldObject = () => {
      if (isReschedulingFlow) return { inviteeUuid: invitee.uuid }
      return { eventTypeUuid: eventType.uuid }
    }

    return {
      ...idFieldObject,
      sendWith: null,
      to,
      cc,
      showCc: false,
      bcc: [],
      showBcc: false,
      subject: prefilledSubject || subject,
      body: prefilledBody || body,
      timeSlotSuggestions: null,
      remindInDays: 3,
      isReminderActive: false,
      isToFieldLocked
    }
  }
)
